/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { Suspense, lazy, useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useRegionSelection } from 'modules/login/RegionSelector/utils';
import Login from 'pages/login/Login';
import PreLoader from 'pages/preloader/PreLoader';
import SignupEmbedded from 'pages/signup/signupEmbedded';
import SignupEmbeddedHandler from 'pages/signup/signupEmbeddedHandler';
import BaseProvider from 'providers/BaseProvider';
import { setChrPrefs } from 'utils/browser';
import { emailLoginConfigs, availableLoginRegions } from './config';
import Signup from './pages/signup/signup';

const DeferredRoot = lazy(
  () => import('modules/deferredRoot' /* webpackChunkName: "DeferredRoot" */)
);

const App: React.FC = () => {
  const { selectedRegion } = useRegionSelection(availableLoginRegions);

  useEffect(() => {
    if (selectedRegion) {
      setChrPrefs(
        { region: selectedRegion },
        { domain: '.choreo.dev', secure: true, sameSite: 'lax' }
      );
    }
  }, [selectedRegion]);

  return (
    <>
      <BaseProvider>
        <Switch>
          <Route path="/login">
            <Login />
          </Route>
          <Route path="/signup_embedded">
            <SignupEmbedded />
          </Route>
          <Route path="/signup_embedded_contest">
            <SignupEmbedded />
          </Route>
          <Route path="/signup_embedded_handler">
            <SignupEmbeddedHandler />
          </Route>
          {emailLoginConfigs.ENABLE_EMAIL_LOGIN === 'true' && (
            <Route path="/signup">
              <Signup />
            </Route>
          )}
          <Route path="*">
            <Suspense fallback={<PreLoader />}>
              <DeferredRoot />
            </Suspense>
          </Route>
        </Switch>
      </BaseProvider>
    </>
  );
};

export default App;
